import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectSearch } from 'src/store/selectors/search';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { setSearch } from 'src/store/actions/search';
import { PATHS, ICONS } from 'src/common/constants';
import { onRequestPhoneCall } from 'src/helpers/validationHelper';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import { enrichContactDataForEdit } from 'src/utils/CommonHelpers/commonhelpers';
import { ITableColumn } from 'src/components/Table/types';
import { createSuiteUsersTableColumns } from 'src/components/SuiteUsersTable/helpers';
import { useSuiteUsersListOperations } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/useSuiteUsersListOperations';
import { useContactFormSubmissionSuites } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/useContactFormSubmissionSuites';
import {
  DeleteContactResult,
  FetchContactByIdResult,
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import { SuiteUserRow } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/types';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';

export interface UseSuiteUsersPageOrchestratorReturn {
  suiteUsers: SuiteUserRow[];
  hasAssignedStrataManager: boolean;
  loading: boolean;
  formLoaderVisible: boolean;
  showOverlay: boolean;
  editingExistingContactInTable: boolean;
  allowedContactFormRoles: string[];
  canAddContact: boolean;
  columns: ITableColumn[];
  searchIsActive: string;
  suite: any;
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  onOpenAddContact: () => void;
  onEditContact: (contactId: string) => Promise<void>;
  onCloseContactForm: () => void;
  onSubmitContactForm: (
    formData: IContactFormData,
    formType: string
  ) => Promise<void>;
  onDeleteContact: (contact: SuiteUserRow) => Promise<void>;
}

const DEFAULT_CONTACT_FORM_ROLES: string[] = [
  'Home Owner',
  'Occupant',
  'Realtor',
];

const ROLES_THAT_CAN_ADD_SUITE_PEOPLE: string[] = [
  'Admin',
  'Super Admin',
  'Property Developer',
];

interface UseSuiteUsersPageOrchestratorParams {
  suite: any;
  property: any;
  onFetchSuiteData: () => void;
}

export const useSuiteUsersPageOrchestrator = ({
  suite,
  property,
  onFetchSuiteData,
}: UseSuiteUsersPageOrchestratorParams): UseSuiteUsersPageOrchestratorReturn => {
  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);
  const formLoaderVisible: boolean = !!useSelector(selectFormLoader);
  const dispatch = useDispatch();

  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [editingExistingContactInTable, setEditingExistingContactInTable] =
    useState<boolean>(false);
  const [editingContactRole, setEditingContactRole] = useState<string | null>(
    null
  );

  const {
    clearContactFormData,
    setContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const { suiteUsers, isFetchingSuiteUsers, fetchSuiteUsers } =
    useSuiteUsersListOperations();

  const contactApi: UseContactAPIReturn = useContactAPI(suite.id);

  const userRoleName: string = user?.roles?.[0]?.role?.name;

  const theme: string | null = localStorage.getItem('theme');

  const isHomeOwnerOrOccupant: boolean =
    userRoleName === 'Home Owner' || userRoleName === 'Occupant';

  // legacy/workaround: tenant-specific exceptions that let unit-scoped roles
  // add people on the Suite, overriding the standard admin-only gate.
  const themeAllowsUnitRoleToAddContact: boolean =
    (userRoleName === 'Home Owner' && theme === 'Albaster') ||
    (isHomeOwnerOrOccupant && theme === 'Rize');

  const canAddContact: boolean =
    ROLES_THAT_CAN_ADD_SUITE_PEOPLE.includes(userRoleName) ||
    themeAllowsUnitRoleToAddContact;

  const allowedContactFormRoles: string[] =
    editingExistingContactInTable && editingContactRole
      ? [editingContactRole]
      : DEFAULT_CONTACT_FORM_ROLES;

  const searchIsActive: string = search?.suiteUsers || '';

  useEffect(() => {
    if (!suite?.id || !user?.roles?.length) {
      return;
    }

    void fetchSuiteUsers(suite.id);
  }, [suite?.id, user?.id]);

  const onCloseContactForm = (): void => {
    setShowOverlay(false);
    clearContactFormData();
    setEditingExistingContactInTable(false);
    setEditingContactRole(null);
  };

  const { handleSubmitContactForm: onSubmitContactForm } =
    useContactFormSubmissionSuites({
      propertyId: suite.propertyId,
      suiteId: suite.id,
      onSuccess: () => {
        onFetchSuiteData();
        void fetchSuiteUsers(suite.id);
      },
      onClose: onCloseContactForm,
    });

  const onOpenAddContact = (): void => {
    clearContactFormData();

    setContactFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      type: 'default',
      userRole: 'default',
      isPrimaryContact: false,
      suiteId: suite.id,
      propertyId: suite.propertyId,
      properties: [property],
      Suites: [
        {
          Id: suite.id,
          name: suite.number || suite.name,
          propertyId: suite.propertyId,
        },
      ],
    });

    setShowOverlay(true);
  };

  const onEditContact = async (contactId: string): Promise<void> => {
    setEditingExistingContactInTable(true);

    const result: FetchContactByIdResult =
      await contactApi.fetchContactById(contactId);

    if (!result.ok || !result.contact) {
      toast.error('🚫 Failed to load contact details. Please try again.', {
        autoClose: 4000,
        position: 'top-center',
      });

      setEditingExistingContactInTable(false);
      return;
    }

    const contact: any = result.contact;
    const enrichedContactData: any = enrichContactDataForEdit(contact);

    setEditingContactRole(contact.roles?.[0]?.role?.name || null);

    initializeContactFormDataFromEdit({
      ...enrichedContactData,
      suiteId: suite.id,
      propertyId: property.propertyId,
    });

    setShowOverlay(true);
  };

  const onDeleteContact = async (contact: SuiteUserRow): Promise<void> => {
    const result: DeleteContactResult = await contactApi.deleteContact(
      contact.id
    );

    if (!result.ok) {
      return;
    }

    onFetchSuiteData();
    await fetchSuiteUsers(suite.id);
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(setSearch({ suiteUsers: e.target.value.trim() }));
  };

  const columns: ITableColumn[] = useMemo(
    () =>
      createSuiteUsersTableColumns({
        user,
        handleSetContactFormData: onEditContact,
        PATHS,
        ICONS,
        onRequestPhoneCall,
        suite,
        property,
      }),
    [user, suite, property]
  );

  const hasAssignedStrataManager: boolean = useMemo(
    () =>
      suiteUsers.some(
        (suiteUser: SuiteUserRow) => suiteUser.roleName === 'Strata Manager'
      ),
    [suiteUsers]
  );

  const loading: boolean = isFetchingSuiteUsers;

  return {
    suiteUsers,
    hasAssignedStrataManager,
    loading,
    formLoaderVisible,
    showOverlay,
    editingExistingContactInTable,
    allowedContactFormRoles,
    canAddContact,
    columns,
    searchIsActive,
    suite,
    onSearch,
    onOpenAddContact,
    onEditContact,
    onCloseContactForm,
    onSubmitContactForm,
    onDeleteContact,
  };
};
